import { useState } from "react";
import { Button } from "@/components/ui/button";
import { FiMail, FiSmartphone, FiFileText, FiArrowRight } from "react-icons/fi";

interface NextStepsSectionProps {
  onSave: () => Promise<void>;
  onNext: () => void;
}

export function NextStepsSection({ onSave, onNext }: NextStepsSectionProps) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleContinue = async () => {
    setIsSaving(true);
    try {
      await onSave();
      onNext();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-serif">What Happens Next</h2>
        <p className="text-muted-foreground mt-1">
          Here's what to expect after you submit your details.
        </p>
      </div>

      {/* Process Steps */}
      <div className="space-y-4">
        <div className="flex gap-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
          <div className="shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <FiArrowRight className="size-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-medium text-blue-900">We'll Submit Your Information</h3>
            <p className="text-sm text-blue-800 mt-1">
              The information you provide will be entered into our brokerage system at{" "}
              <a
                href="https://resi.brokerfactfind.com.au"
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-medium hover:text-blue-600"
              >
                resi.brokerfactfind.com.au
              </a>
              .
            </p>
          </div>
        </div>

        <div className="flex gap-4 p-4 bg-green-50 rounded-lg border border-green-100">
          <div className="shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <FiMail className="size-5 text-green-600" />
          </div>
          <div>
            <h3 className="font-medium text-green-900">You'll Receive an Email</h3>
            <p className="text-sm text-green-800 mt-1">
              Once we submit your data into our system, you will receive an email notification
              confirming the submission.
            </p>
          </div>
        </div>

        <div className="flex gap-4 p-4 bg-purple-50 rounded-lg border border-purple-100">
          <div className="shrink-0 w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
            <FiFileText className="size-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-medium text-purple-900">Complete the Full Fact Finder</h3>
            <p className="text-sm text-purple-800 mt-1">
              You will receive a full Fact Finder document that covers all the detailed
              financial information we need to find you the best loan options.
            </p>
          </div>
        </div>

        <div className="flex gap-4 p-4 bg-amber-50 rounded-lg border border-amber-100">
          <div className="shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
            <FiSmartphone className="size-5 text-amber-600" />
          </div>
          <div>
            <h3 className="font-medium text-amber-900">PIN Verification via Mobile</h3>
            <p className="text-sm text-amber-800 mt-1">
              <strong>Important:</strong> Please ensure you have entered the correct mobile number.
              A PIN will be sent to your mobile to verify your identity and start the process.
            </p>
          </div>
        </div>
      </div>

      {/* Acknowledgment */}
      <div className="bg-muted p-4 rounded-lg">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="w-5 h-5 rounded border-gray-300 mt-0.5"
          />
          <span className="text-sm">
            I understand that my information will be submitted to the brokerage system and I will
            receive further communication via email and SMS to complete the full application process.
          </span>
        </label>
      </div>

      {/* Actions */}
      <div className="flex justify-end pt-4 border-t">
        <Button onClick={handleContinue} disabled={!acknowledged || isSaving} size="lg">
          {isSaving ? "Saving..." : "Continue to Consent"}
        </Button>
      </div>
    </div>
  );
}
